'use client';

import { FileItem } from '@magicschool/spellbook/components/FileItem';
import { ControlledModalTrigger } from '@magicschool/spellbook/components/Modal';
import { Slot } from '@magicschool/spellbook/components/Slot';
import { Spinner } from '@magicschool/spellbook/components/Spinner';
import { useId } from '@magicschool/spellbook/hooks/useId';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import type { ReactNode } from 'react';
import { FilePreviewCornerAction } from './FilePreviewCornerAction';
import type { FilePreviewChipBaseProps, FilePreviewChipInjectedProps } from './types';
import { UploadCompletionLiveRegion } from './UploadCompletionLiveRegion';
import { useUploadCompletionMessage } from './useUploadCompletionMessage';

// ─── FilePreview.Expanded ────────────────────────────────────────────────────

/**
 * Override the default expanded view rendered in the Viewer modal for a Document chip.
 * Place as a child of `FilePreview.Document` alongside `expandOnClick`.
 *
 * ```tsx
 * <FilePreview.Document name="Lesson.pdf" fileType="pdf" expandOnClick>
 *   <FilePreview.Expanded>
 *     <MyPdfViewer src={url} />
 *   </FilePreview.Expanded>
 * </FilePreview.Document>
 * ```
 */
export const FilePreviewExpandedSlot = Slot<{ children?: ReactNode }>({
  displayName: 'FilePreview.Expanded',
  spellbookName: false,
  renderDefault: ({ children }) => <>{children}</>,
});

// ─── FilePreview.Image ───────────────────────────────────────────────────────

export interface FilePreviewImageProps extends FilePreviewChipBaseProps, FilePreviewChipInjectedProps {
  src: string;
  /**
   * Accessible description of the image. Supply a value to give the remove, abort, and expand
   * buttons accessible names that include the file description (e.g. "Remove Screenshot.png").
   */
  alt?: string;
  /**
   * Display name shown as the modal header when `expandOnClick` opens the viewer.
   * Defaults to `alt` when provided, otherwise `"Image"`. Pass a translated string for i18n.
   */
  name?: string;
}

export const FilePreviewImageSlot = Slot<FilePreviewImageProps>({
  displayName: 'FilePreview.Image',
  preventOverride: true,
  renderDefault: ({
    src = '',
    alt = '',
    name,
    onRemove,
    disabled,
    expandOnClick = false,
    isProcessing = false,
    onAbort,
    children,
    text: textProp,
    'data-testid': dataTestId,
    'data-file-chip-id': dataFileChipId,
    openModal,
    isModalOpen = false,
    parentText,
    parentDisabled,
  }) => {
    const isDisabled = parentDisabled || disabled;

    const text = {
      remove: 'Remove',
      expand: 'View larger',
      closeModal: 'Close preview',
      processing: 'Uploading...',
      abort: 'Cancel upload',
      uploadComplete: 'Upload complete',
      ...parentText,
      ...textProp,
    };

    const { slots } = useSlots(children, { expanded: { Component: FilePreviewExpandedSlot } });

    const imageId = useId();
    const removeLabelId = useId();
    const expandLabelId = useId();
    const abortLabelId = useId();
    const completionMessage = useUploadCompletionMessage(isProcessing, alt ? `${text.uploadComplete}: ${alt}` : text.uploadComplete);

    const expandClickActive = expandOnClick && !isProcessing && !isDisabled && !!openModal;

    const defaultExpandedContent = <img src={src} alt={alt} className="max-w-full rounded-sm" />;

    const thumbnail = (
      <div
        className={cn(
          'relative w-full h-full rounded-md overflow-hidden border border-border-default',
          isProcessing && 'bg-surface-background-1',
        )}
      >
        <img
          id={imageId}
          src={src}
          alt={alt}
          className={cn('object-cover w-full h-full transition-opacity duration-150', isProcessing && 'opacity-25 saturate-50')}
        />
        {isProcessing && (
          <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <Spinner size="sm" color="default" text={{ loading: text.processing }} />
          </span>
        )}
      </div>
    );

    const expandButtonClassName =
      'w-full h-full block rounded-md cursor-zoom-in outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-1 focus-visible:ring-offset-surface-background-0';

    const trigger = expandClickActive ? (
      <ControlledModalTrigger
        modalOpen={isModalOpen}
        onClick={() =>
          openModal({
            kind: 'image',
            title: name ?? (alt || 'Image'),
            closeModalLabel: text.closeModal,
            expandedContent: slots.expanded ?? defaultExpandedContent,
          })
        }
      >
        <button type="button" aria-labelledby={`${expandLabelId} ${imageId}`} className={expandButtonClassName}>
          {thumbnail}
        </button>
      </ControlledModalTrigger>
    ) : (
      thumbnail
    );

    return (
      <div
        data-spellbook-name="file-preview-image"
        className="group relative w-11 h-11 flex-shrink-0"
        data-testid={dataTestId}
        data-file-chip-id={dataFileChipId}
      >
        {trigger}
        {!isProcessing && !isDisabled && onRemove && (
          <>
            <span id={removeLabelId} className="sr-only">
              {text.remove}
            </span>
            <FilePreviewCornerAction icon="x" ariaLabelledBy={`${removeLabelId} ${imageId}`} onClick={onRemove} />
          </>
        )}
        {isProcessing && !isDisabled && onAbort && (
          <>
            <span id={abortLabelId} className="sr-only">
              {text.abort}
            </span>
            <FilePreviewCornerAction icon="circle-stop" alwaysVisible ariaLabelledBy={`${abortLabelId} ${imageId}`} onClick={onAbort} />
          </>
        )}
        {expandClickActive && (
          <span id={expandLabelId} className="sr-only">
            {text.expand}
          </span>
        )}
        <UploadCompletionLiveRegion message={completionMessage} />
      </div>
    );
  },
});

// ─── FilePreview.Document ────────────────────────────────────────────────────

export interface FilePreviewDocumentProps extends FilePreviewChipBaseProps, FilePreviewChipInjectedProps {
  name: string;
  /** File type extension, e.g. `"pdf"`, `"docx"`. */
  fileType: string;
  /** Optional description line shown below the file name (e.g. word count, page count, file size). */
  description?: string;
  /**
   * Terminal failure signal. When set (without `isProcessing`), the chip renders nothing —
   * failed uploads are expected to be removed by the consumer rather than lingering.
   */
  error?: string;
}

export const FilePreviewDocumentSlot = Slot<FilePreviewDocumentProps>({
  displayName: 'FilePreview.Document',
  preventOverride: true,
  renderDefault: ({
    name = '',
    fileType = '',
    description,
    onRemove,
    isProcessing = false,
    onAbort,
    error,
    disabled,
    expandOnClick = false,
    children,
    text: textProp,
    'data-testid': dataTestId,
    'data-file-chip-id': dataFileChipId,
    openModal,
    isModalOpen = false,
    parentText,
    parentDisabled,
  }) => {
    const isDisabled = parentDisabled || disabled;

    const text = {
      remove: 'Remove',
      processing: 'Processing...',
      abort: 'Cancel upload',
      uploadComplete: 'Upload complete',
      expand: 'View document',
      closeModal: 'Close preview',
      ...parentText,
      ...textProp,
    };

    const titleId = useId();
    const removeLabelId = useId();
    const abortLabelId = useId();
    const expandLabelId = useId();
    const completionMessage = useUploadCompletionMessage(isProcessing || !!error, `${text.uploadComplete}: ${name}`);
    const { slots } = useSlots(children, { expanded: { Component: FilePreviewExpandedSlot } });

    if (error && !isProcessing) return null;

    const showDescription = isProcessing || !!description;
    const expandClickActive = expandOnClick && !isProcessing && !isDisabled && !!openModal;

    const defaultExpandedContent = (
      <FileItem variant="card" fileType={fileType}>
        <FileItem.FileType>{fileType}</FileItem.FileType>
        <FileItem.Title>{name}</FileItem.Title>
        {description && <FileItem.Description>{description}</FileItem.Description>}
      </FileItem>
    );

    return (
      <div
        data-spellbook-name="file-preview-document"
        className="group relative"
        data-testid={dataTestId}
        data-file-chip-id={dataFileChipId}
      >
        <FileItem variant="card" fileType={fileType} disabled={isDisabled} isLoading={isProcessing} text={{ loading: text.processing }}>
          <FileItem.FileType>{fileType}</FileItem.FileType>
          <FileItem.Title id={titleId}>{name}</FileItem.Title>
          {showDescription && <FileItem.Description>{description}</FileItem.Description>}
        </FileItem>
        {expandClickActive && (
          <>
            <span id={expandLabelId} className="sr-only">
              {text.expand}
            </span>
            <ControlledModalTrigger
              modalOpen={isModalOpen}
              onClick={() =>
                openModal({
                  kind: 'document',
                  name,
                  closeModalLabel: text.closeModal,
                  expandedContent: slots.expanded ?? defaultExpandedContent,
                })
              }
            >
              <button
                type="button"
                aria-labelledby={`${expandLabelId} ${titleId}`}
                className="absolute inset-0 rounded-card-md cursor-zoom-in outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-1"
              />
            </ControlledModalTrigger>
          </>
        )}
        {!isProcessing && !isDisabled && onRemove && (
          <>
            <span id={removeLabelId} className="sr-only">
              {text.remove}
            </span>
            <FilePreviewCornerAction icon="x" ariaLabelledBy={`${removeLabelId} ${titleId}`} onClick={onRemove} />
          </>
        )}
        {isProcessing && !isDisabled && onAbort && (
          <>
            <span id={abortLabelId} className="sr-only">
              {text.abort}
            </span>
            <FilePreviewCornerAction icon="circle-stop" alwaysVisible ariaLabelledBy={`${abortLabelId} ${titleId}`} onClick={onAbort} />
          </>
        )}
        <UploadCompletionLiveRegion message={completionMessage} />
      </div>
    );
  },
});
